import React, { useEffect, useState } from "react";
import { Modal, Form, Input, InputNumber, Button, Divider, Select } from "antd";
import {
  GlobalOutlined,
  SaveOutlined,
} from "@ant-design/icons";

import "./addCurrency.css";

const CountryEditModal = ({
  visible,
  onClose,
  countryData,
  onSave,
  enumsData = {},
}) => {
  const [form] = Form.useForm();
  // Track selected currency to trigger conditional rendering
  const [selectedCurrency, setSelectedCurrency] = useState(countryData?.currency || "");

  const currencyEnumList = enumsData?.enumsData?.data?.currency
    ? Object.entries(enumsData.enumsData.data.currency).map(([key, value]) => ({
        code: key,
        name: value,
      }))
    : [];

  useEffect(() => {
    if (visible && countryData) {
      form.setFieldsValue(countryData);
      setSelectedCurrency(countryData.currency || "");
    }
  }, [visible, countryData, form]);

  // Update selectedCurrency when the currency field value changes
  const onValuesChange = (changedValues) => {
    if (changedValues.currency) {
      setSelectedCurrency(changedValues.currency);
    }
  };

  const handleFinish = (values) => {
    onSave(values);
  };

  return (
    <Modal
      title={
        <div className="country-edit-modal-header-content">
          <GlobalOutlined className="country-edit-modal-header-icon" />
          <span style={{ color: "white" }}>
            ADD Currency
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={600}
      className="country-edit-modal-override"
      styles={{
        header: {
          background: "black",
          borderRadius: "12px 12px 0 0",
          padding: "20px 24px",
          borderBottom: "none",
        },
        body: {
          padding: "24px",
          background: "#fafafa",
        },
      }}
    >
      <div className="country-edit-modal-form">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          onValuesChange={onValuesChange}
        >
          {/* Basic Information Section */}
          <div className="country-edit-section">
            <h3 className="country-edit-section-header">Basic Information</h3>
            <div className="country-edit-grid">
              <Form.Item
                label="Country Name"
                name="name"
                rules={[{ required: true, message: "Please input country name" }]}
              >
                <Input
                  size="large"
                  placeholder="Enter country name"
                  className="country-edit-input-override"
                />
              </Form.Item>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true, message: "Please select currency" }]}
              >
                <Select
                  size="large"
                  placeholder="Select Currency"
                  className="country-edit-input-override"
                  options={currencyEnumList.map((currency) => ({
                    label: currency.name,
                    value: currency.code,
                  }))}
                />
              </Form.Item>
            </div>
          </div>

          <Divider className="country-edit-divider" />

          {/* Booking Configuration Section */}
          <div className="country-edit-section">
            <h3 className="country-edit-section-header">Booking Configuration</h3>
            <Form.Item
              label="Booking Amount Percentage (%)"
              name="booking_amount_percent"
              rules={[
                { required: true, message: "Please input booking amount percent" },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                size="large"
                placeholder="0.00"
                style={{ width: "100%" }}
                className="country-edit-input-override"
              />
            </Form.Item>

            <div className="country-edit-grid">
              <Form.Item
                label="Min Booking Amount"
                name="min_booking_amount"
                rules={[
                  { required: true, message: "Please input min booking amount" },
                ]}
              >
                <InputNumber
                  min={0}
                  size="large"
                  placeholder="0.00"
                  formatter={(value) =>
                    value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/(,*)/g, "")}
                  style={{ width: "100%" }}
                  className="country-edit-input-override"
                />
              </Form.Item>

              <Form.Item
                label="Max Booking Amount"
                name="max_booking_amount"
                rules={[
                  { required: true, message: "Please input max booking amount" },
                ]}
              >
                <InputNumber
                  min={0}
                  size="large"
                  placeholder="0.00"
                  formatter={(value) =>
                    value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/(,*)/g, "")}
                  style={{ width: "100%" }}
                  className="country-edit-input-override"
                />
              </Form.Item>
            </div>

            {/* Conditionally render Max Property Price when currency is INR */}
            {selectedCurrency === "INR" && (
              <Form.Item
                label="Max Property Price"
                name="max_property_price"
                rules={[
                  { required: true, message: "Please input max property price" },
                ]}
              >
                <InputNumber
                  min={0}
                  size="large"
                  placeholder="0.00"
                  formatter={(value) =>
                    value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/(,*)/g, "")}
                  style={{ width: "100%" }}
                  className="country-edit-input-override"
                />
              </Form.Item>
            )}
          </div>

          <Divider className="country-edit-divider" />

          {/* Kiosk Settings Section */}
          <div className="country-edit-section">
            <h3 className="country-edit-section-header">Kiosk</h3>

            <div className="country-edit-grid">
              <Form.Item label="Min Booking Amount" name="min_booking_amount_kiosk">
                <InputNumber
                  min={0}
                  size="large"
                  placeholder="0.00"
                  formatter={(value) =>
                    value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/(,*)/g, "")}
                  style={{ width: "100%" }}
                  className="country-edit-input-override"
                />
              </Form.Item>

              <Form.Item label="Max Booking Amount" name="max_booking_amount_kiosk">
                <InputNumber
                  min={0}
                  size="large"
                  placeholder="0.00"
                  formatter={(value) =>
                    value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/(,*)/g, "")}
                  style={{ width: "100%" }}
                  className="country-edit-input-override"
                />
              </Form.Item>
            </div>
          </div>

          {/* Action Buttons */}
          <Form.Item style={{ marginBottom: 0 }}>
            <div className="country-edit-actions">
              <Button
                size="large"
                onClick={onClose}
                className="country-edit-btn country-edit-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                htmlType="submit"
                size="large"
                icon={<SaveOutlined />}
                className="country-edit-btn country-edit-btn-primary country-edit-btn-override"
              >
                Save Changes
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default CountryEditModal;
